import { useRef, type MouseEvent } from 'react';
import { Box, Button, type ButtonProps } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';

interface SubmitButtonProps extends ButtonProps {
  /**
   * What the button is doing while `loading` ("Importing…"): its accessible name and the polite
   * status. Default: the label stays, and the status says "Saving…".
   */
  loadingLabel?: string;
}

/**
 * Every dialog's primary action (TMM-B4, TMMAPP-0071): MUI `Button` with `loading`, plus a
 * visually hidden polite `role="status"` that says "Saving…" (or `loadingLabel`) while it is in
 * flight. The theme keeps a loading contained button on its own fill (TMMAPP-0097 B3). A repeat
 * press is ignored — while `loading`, and within the same task as the first press (a burst of
 * clicks lands before the caller's busy state renders).
 */
export function SubmitButton({
  loading = false,
  loadingLabel,
  onClick,
  variant = 'contained',
  children,
  ...buttonProps
}: SubmitButtonProps) {
  const { t } = useTranslation();
  const pressed = useRef(false);

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    if (loading || pressed.current) {
      // Also stops a `type="submit"` press from submitting its form again.
      event.preventDefault();
      return;
    }
    pressed.current = true;
    queueMicrotask(() => {
      pressed.current = false;
    });
    onClick?.(event);
  }

  return (
    <>
      <Button {...buttonProps} variant={variant} loading={loading} onClick={handleClick}>
        {loading && loadingLabel !== undefined ? loadingLabel : children}
      </Button>
      <Box component="span" role="status" aria-live="polite" sx={visuallyHidden}>
        {loading ? (loadingLabel ?? t('common.saving')) : ''}
      </Box>
    </>
  );
}
